import { useState } from 'react';
import dayjs from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import { Tooltip } from '@mui/material';
import { Badge, CustomButton, Text } from '../../../shared/uiComponents';
import { Trash } from '../../../shared/Assets/svgIcons';
import { AnnotationDateProperties } from '../../../redux/API/ClientAPIHelpers/graphProperties';
import { AnnotationBehaviorDate } from '../../../redux/API/ClientAPIHelpers/graphAnnotationProperties';
import {
	deleteAnnotation,
	deleteBehaviorAnnotation,
	getBehaviorReductionAllAnnotations,
	getTargetAllAnnotations,
} from '../../../redux/State/clientSlice/graphAnnotationSlice';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { DispatchProperties, useSelector } from '../../../redux/store';

interface ViewAnnotationRowProps {
	clientId: string;
	item: AnnotationDateProperties | AnnotationBehaviorDate;
	type: 'target' | 'behavior';
}
interface HandleDeleteAnnotationProperties {
	annotationId: string;
	deleteGlobally: boolean;
}

export const ViewAnnotationRow = ({ clientId, item, type }: ViewAnnotationRowProps) => {
	const [removeConfirm, setRemoveConfirm] = useState<boolean>(false);
	const { isGlobal } = item;
	const dispatch = useDispatch<DispatchProperties>();
	const isDeleting = useSelector((state) => state.graphAnnotation.isDeleting);

	const handleDeleteAnnotation = ({ annotationId, deleteGlobally }: HandleDeleteAnnotationProperties) => {
		if (!annotationId) return;

		if (type === 'target') {
			const { targetId } = item as AnnotationDateProperties;

			const parameters = deleteGlobally ? { annotationId } : { annotationId, targetId };

			dispatch(deleteAnnotation({ ...parameters }))
				.then(unwrapResult)
				.then(() => {
					dispatch(getTargetAllAnnotations(clientId));
					toast('Deleted');
				})
				.catch(errorNormalizer);
		} else {
			const { behaviorReductionId } = item as AnnotationBehaviorDate;
			const parameters = deleteGlobally ? { annotationId } : { annotationId, behaviorId: behaviorReductionId };
			dispatch(deleteBehaviorAnnotation({ ...parameters }))
				.then(unwrapResult)
				.then(() => {
					dispatch(getBehaviorReductionAllAnnotations(clientId));
					toast('Deleted');
				})
				.catch(errorNormalizer);
		}
	};

	return (
		<div className="graphAnnotationInfoItem" key={item.id}>
			<div style={{ display: 'flex', width: '100%' }}>
				<div
					style={{
						width: '50%',
						marginRight: '8px',
						display: 'flex',
						marginBottom: '8px',
					}}
				>
					<Text title={item.note} type="psm" style={{ marginRight: '8px' }} />
					{isGlobal && <Badge label="Global" sizeType="sm" type="primary" />}
				</div>
				<Text title={dayjs(new Date(item.date)).format('MM-DD-YYYY')} type="psm" style={{ width: '30%' }} />
				{!removeConfirm && (
					<Tooltip title="Delete">
						<div onClick={() => setRemoveConfirm(true)} style={{ cursor: 'pointer' }}>
							<Trash />
						</div>
					</Tooltip>
				)}
			</div>
			{removeConfirm && (
				<div
					style={{
						width: '100%',
						marginBottom: '8px',
						padding: '16px',
						border: '1px solid #E0E0E0',
						borderRadius: '4px',
						backgroundColor: '#FAFAFA',
						justifyContent: 'center',
					}}
				>
					<Text
						title={
							isGlobal
								? 'Remove this annotation from only this graph or from Аll graphs?'
								: 'Are you sure you want to delete this annotation?'
						}
						type="psm"
						fontWeight="medium"
						style={{
							textAlign: 'center',
						}}
					/>
					<div
						style={{
							display: 'flex',
							width: '100%',
							justifyContent: 'center',
							marginTop: '16px',
						}}
					>
						<CustomButton
							title={isGlobal ? 'Only This Graph' : 'Delete'}
							onClick={() =>
								handleDeleteAnnotation({
									annotationId: item.id,
									deleteGlobally: false,
								})
							}
							className="graphDeleteActionButton"
							disabled={isDeleting}
							loading={isDeleting}
						/>
						{isGlobal && (
							<CustomButton
								title="All Graphs"
								onClick={() =>
									handleDeleteAnnotation({
										annotationId: item.id,
										deleteGlobally: true,
									})
								}
								className="graphDeleteActionButton"
								buttonType="secondary"
								disabled={isDeleting}
								loading={isDeleting}
							/>
						)}

						<CustomButton title="Cancel" onClick={() => setRemoveConfirm(false)} buttonType="secondaryGray" />
					</div>
				</div>
			)}
		</div>
	);
};
